import { useEffect, useState } from "react";
import { productsApi, type Product } from "@/lib/api";
import { ProductCard } from "@/components/ProductCatalog/ProductCard";
import { ProductFilters } from "@/components/ProductCatalog/ProductFilters";
import { Package, TrendingUp, Filter } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

import { Layout } from "@/components/Layout";

const ProductCatalog = () => {
  const [data, setData] = useState<Product[]>([]);
  const [search, setSearch] = useState("");
  const [selectedFilters, setSelectedFilters] = useState({
    utilizacao: [] as string[],
    ano_letivo: [] as number[],
    serie: [] as string[],
    status: [] as string[]
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await productsApi.getAll();
        setData(response.data || []);
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    };

    fetchData();
  }, []);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const handleFilterChange = (filterType: string, value: string | number) => {
    setSelectedFilters(prev => {
      const currentFilter = prev[filterType as keyof typeof prev] as (string | number)[];
      const isSelected = currentFilter.includes(value);
      
      if (isSelected) {
        return {
          ...prev,
          [filterType]: currentFilter.filter(item => item !== value)
        };
      } else {
        return {
          ...prev,
          [filterType]: [...currentFilter, value]
        };
      }
    });
  };

  const clearFilters = () => {
    setSelectedFilters({
      utilizacao: [],
      ano_letivo: [],
      serie: [],
      status: []
    });
    setSearch("");
  };

  const filteredData = data.filter((item) => {
    const searchTerms = search.toLowerCase().split(" ");
    const itemValues = [
      item.codigo_produto,
      item.descricao_produto,
      item.utilizacao,
      item.ano_letivo?.toString(),
      item.serie,
      item.sku,
      item.preco.toString(),
      item.status,
    ].map((value) => String(value || '').toLowerCase());

    const matchesSearch = searchTerms.every((term) =>
      itemValues.some((value) => value.includes(term))
    );

    const matchesFilters = 
      (selectedFilters.utilizacao.length === 0 || selectedFilters.utilizacao.includes(item.utilizacao || '')) &&
      (selectedFilters.ano_letivo.length === 0 || selectedFilters.ano_letivo.includes(item.ano_letivo)) &&
      (selectedFilters.serie.length === 0 || selectedFilters.serie.includes(item.serie || '')) &&
      (selectedFilters.status.length === 0 || selectedFilters.status.includes(item.status));

    return matchesSearch && matchesFilters;
  });

  const stats = {
    total: data.length,
    active: data.filter(p => p.status === 'Ativo').length,
    filtered: filteredData.length
  };

  return (
    <Layout title="Catálogo de Produtos">
      <div className="space-y-8">
        <div className="text-center space-y-4">
          <div className="flex items-center justify-center gap-3">
            <div className="p-3 rounded-full bg-primary/10">
              <Package className="h-8 w-8 text-primary" />
            </div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
              Catálogo de Produtos
            </h1>
          </div>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Explore nossa coleção completa de materiais educacionais com filtros inteligentes e busca avançada
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Total de Produtos</p>
                  <p className="text-3xl font-bold text-blue-600">{stats.total}</p>
                </div>
                <Package className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Produtos Ativos</p>
                  <p className="text-3xl font-bold text-green-600">{stats.active}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-primary">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Exibindo</p>
                  <p className="text-3xl font-bold text-primary">{stats.filtered}</p>
                </div>
                <Filter className="h-8 w-8 text-primary" />
              </div>
            </CardContent>
          </Card>
        </div>

        <ProductFilters
          data={data}
          search={search}
          onSearchChange={setSearch}
          selectedFilters={selectedFilters}
          onFilterChange={handleFilterChange}
          onClearFilters={clearFilters}
        />

        {filteredData.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="p-12 text-center">
              <Package className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Nenhum produto encontrado</h3>
              <p className="text-muted-foreground">
                Tente ajustar seus filtros ou termo de busca para encontrar produtos
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredData.map((product: any) => product.isActive && (
              <ProductCard
                key={product.id}
                product={product}
                formatCurrency={formatCurrency}
              />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ProductCatalog;
